import { useState } from 'react';

import { useGetProjectCollections } from 'modules/data';
import { useI18n } from 'modules/i18n';
import PropTypes from 'prop-types';

import { useCollectionsSort, useFilteredCollections } from '../hooks';
import { sortCollections } from '../utils';
import { CollectionCard } from './CollectionCard';
import { CollectionSortControl } from './CollectionSortControl';

const INITIAL_VISIBLE_COLLECTIONS = 10;

export function CollectionList({ project, query = '' }) {
    const { t } = useI18n();
    const { collections } = useGetProjectCollections(project.id, {
        all: true,
        workflowState: 'public',
    });
    const [showAllCollections, setShowAllCollections] = useState(false);
    const { sort, setSort } = useCollectionsSort();
    const filteredCollections = useFilteredCollections({ collections, query });
    const sortedCollections = sortCollections(filteredCollections, sort);

    if (!collections || collections.length === 0) return null;

    const hiddenCollectionsCount = Math.max(
        sortedCollections.length - INITIAL_VISIBLE_COLLECTIONS,
        0
    );
    const hiddenByQueryCount = query
        ? Math.max(collections.length - filteredCollections.length, 0)
        : 0;
    const visibleCollections = showAllCollections
        ? sortedCollections
        : sortedCollections.slice(0, INITIAL_VISIBLE_COLLECTIONS);

    return (
        <div className={'CollectionList'}>
            <div className="CollectionList--filtersInfo">
                <p className="CollectionList--countLabel">
                    {t('explorer.collection_list.title', {
                        count: filteredCollections.length,
                    })}
                    {hiddenByQueryCount > 0 && (
                        <span className="CollectionList-hiddenByFilterInfo">
                            {` – ${hiddenByQueryCount} `}
                            {t('explorer.collection_list.hidden_by_filter', {
                                count: hiddenByQueryCount,
                            })}
                        </span>
                    )}
                </p>
                <CollectionSortControl value={sort} onChange={setSort} />
            </div>
            {visibleCollections.map((collection) => (
                <CollectionCard
                    key={collection.id}
                    collection={collection}
                    project={project}
                    query={query}
                />
            ))}
            {!showAllCollections && hiddenCollectionsCount > 0 && (
                <button
                    type="button"
                    className="CollectionList-showMoreButton"
                    onClick={() => setShowAllCollections(true)}
                    aria-label="Show more collections"
                >
                    +{hiddenCollectionsCount}
                </button>
            )}
        </div>
    );
}

export default CollectionList;

CollectionList.propTypes = {
    project: PropTypes.object.isRequired,
    query: PropTypes.string,
};
